/**
 * ChatbotWidget — storefront AI chatbot orchestrator.
 *
 * Mounts once globally inside StorefrontLayout. Bootstraps a visitor identity
 * (dual-store cookie + localStorage), opens a slide-up panel, and exchanges
 * messages with the merchant's backend chatbot endpoints. Polls every 4s for
 * inbound (merchant/AI) messages while open — replaced by socket in Phase 5.
 *
 * Renders nothing if the store has the chatbot disabled (settings.isEnabled
 * false) or if the bootstrap call fails.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io as socketIo } from 'socket.io-client';
import { MessageCircle, X, Send } from 'lucide-react';
import axiosInstance from '../../../axiosConfig';
import useLiveActivity from './useLiveActivity';
import './Chatbot.css';

// Visitor-facing HTTP client. Uses the same base URL resolution as the
// dashboard axios but WITHOUT credentials/interceptors. The merchant axios
// instance sends cookies + a cart header, which can confuse the visitor JWT
// gate on the public endpoints and trigger silent 401s — keep these calls
// completely isolated.
function buildVisitorHttp() {
  const baseUrl = axiosInstance?.defaults?.baseURL || '/api';
  return axios.create({ baseURL: baseUrl, withCredentials: false, timeout: 30000 });
}

// Socket URL — must connect directly to backend (not through /api proxy).
// Matches the pattern in frontend/src/features/whatsapp/hooks/useSocket.js.
function getSocketUrl() {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:4000';
  }
  return 'https://revstar-main-platform-production.up.railway.app';
}

// ---------- Visitor identity (cookie + localStorage dual-store) -----------

const VISITOR_KEY = 'revstar_visitor_anon_id';
const DISMISSED_SESSION_KEY = 'revstar_chatbot_dismissed';

function generateAnonId() {
  // 32-char hex; uses webcrypto when available, falls back to Math.random
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

function writeCookie(name, value, days = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // Lax is fine — visitor identity is not a credential
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getOrCreateAnonId() {
  const fromLs = (typeof localStorage !== 'undefined' && localStorage.getItem(VISITOR_KEY)) || null;
  const fromCookie = readCookie(VISITOR_KEY);
  const existing = fromLs || fromCookie;
  if (existing) {
    if (!fromLs && typeof localStorage !== 'undefined') localStorage.setItem(VISITOR_KEY, existing);
    if (!fromCookie) writeCookie(VISITOR_KEY, existing);
    return existing;
  }
  const fresh = generateAnonId();
  if (typeof localStorage !== 'undefined') localStorage.setItem(VISITOR_KEY, fresh);
  writeCookie(VISITOR_KEY, fresh);
  return fresh;
}

// ---------- Component -----------------------------------------------------

const POLL_INTERVAL_MS = 4000;

const ChatbotWidget = ({ store }) => {
  const storeSlug = store?.slug;

  const [bootstrap, setBootstrap] = useState(null); // { enabled, visitorId, token, ... }
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(null);

  const tokenRef = useRef(null);          // visitor JWT lives in memory only
  const httpRef = useRef(buildVisitorHttp());
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // --- Bootstrap on mount ---------------------------------------------------

  useEffect(() => {
    if (!storeSlug) return;
    let cancelled = false;

    const run = async () => {
      try {
        const anonId = getOrCreateAnonId();
        const landing = {
          path: window.location.pathname + window.location.search,
          referrer: document.referrer || null,
          language: (navigator.language || '').slice(0, 12) || null,
        };
        // UTM
        const search = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => {
          const v = search.get(k);
          if (v) landing[k] = v.slice(0, 80);
        });

        const res = await httpRef.current.post(`/storefront/${storeSlug}/chatbot/visitor`, {
          anonId,
          landing,
        });

        if (cancelled) return;
        const data = res?.data || {};
        if (!data.success) throw new Error('bootstrap failed');
        if (!data.enabled) {
          setBootstrap({ enabled: false });
          return;
        }
        tokenRef.current = data.token;
        setBootstrap(data);
        if (data.openConversationId) {
          setConversationId(data.openConversationId);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[Chatbot] bootstrap error:', err?.message || err);
        setBootstrapError(err?.message || 'failed');
      }
    };

    run();
    return () => { cancelled = true; };
  }, [storeSlug]);

  // --- Auto-open (once per session) ----------------------------------------
  // Desktop: open immediately on page load — the wide screen has room for it
  //          and visitors land on the home/shop view with intent to chat.
  // Mobile:  respect the configured delay (default 15s) so the panel doesn't
  //          take over the whole screen before the visitor has seen anything.
  // Both honor the per-session dismiss flag so we never re-pop after close.

  useEffect(() => {
    if (!bootstrap?.enabled) return;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DISMISSED_SESSION_KEY)) return;

    const isDesktop = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(min-width: 769px)').matches;

    const configured = parseInt(bootstrap?.settings?.openOnLandingAfterMs ?? 15000, 10);
    const delay = isDesktop ? 0 : Math.max(0, configured);
    if (!Number.isFinite(delay)) return;

    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [bootstrap]);

  // --- Seed greeting message on first open --------------------------------

  useEffect(() => {
    if (!open || !bootstrap?.enabled) return;
    if (messages.length > 0) return;
    const greeting = bootstrap?.settings?.greetingMessage
      || `Hi! Welcome to ${bootstrap.storeName || 'our store'}. Looking for something specific?`;
    setMessages([{
      id: `local-greeting-${Date.now()}`,
      role: 'AI',
      content: greeting,
      createdAt: new Date().toISOString(),
      _local: true,
    }]);
  }, [open, bootstrap, messages.length]);

  // --- Load history when reopening an existing conversation ---------------

  useEffect(() => {
    if (!open || !bootstrap?.enabled || !conversationId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await httpRef.current.get(
          `/storefront/${storeSlug}/chatbot/conversation/${conversationId}`,
          { headers: { Authorization: `Bearer ${tokenRef.current}` } },
        );
        if (cancelled) return;
        const fresh = res?.data?.messages || [];
        if (fresh.length > 0) {
          seenIdsRef.current = new Set(fresh.map((m) => m.id));
          setMessages(fresh);
        }
      } catch (err) {
        console.error('[Chatbot] history load failed:', err?.message);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, bootstrap?.enabled, conversationId, storeSlug]);

  // --- Polling for new messages while open --------------------------------

  const fetchLatest = useCallback(async () => {
    if (!conversationId || !tokenRef.current) return;
    try {
      const res = await httpRef.current.get(
        `/storefront/${storeSlug}/chatbot/conversation/${conversationId}`,
        { headers: { Authorization: `Bearer ${tokenRef.current}` } },
      );
      const fresh = res?.data?.messages || [];
      let merged = false;
      setMessages((prev) => {
        // Replace optimistic + local-only entries; keep server order.
        // If the server already has a VISITOR message whose content matches
        // our local optimistic one, drop the local — otherwise polling
        // during an in-flight POST would briefly render the same text twice.
        const next = fresh.slice();
        const freshVisitorContents = new Set(
          fresh.filter((m) => m.role === 'VISITOR').map((m) => (m.content || '').trim()),
        );
        prev.forEach((m) => {
          if (!m._local || m.role === 'AI') return;
          const sameOnServer = freshVisitorContents.has((m.content || '').trim());
          if (sameOnServer) return;
          if (seenIdsRef.current.has(m.id)) return;
          next.push(m);
        });
        // Detect new messages for "aiTyping" turnoff
        fresh.forEach((m) => {
          if (!seenIdsRef.current.has(m.id)) {
            seenIdsRef.current.add(m.id);
            merged = true;
          }
        });
        return next;
      });
      if (merged) setAiTyping(false);
    } catch (err) {
      // 404 means conversation was deleted — clear and let user start fresh
      if (err?.response?.status === 404) {
        setConversationId(null);
        setMessages([]);
        seenIdsRef.current = new Set();
      }
    }
  }, [conversationId, storeSlug]);

  useEffect(() => {
    if (!open || !conversationId) return;
    // Socket is our primary delivery path; polling stays as a safety net but
    // backs off to 30s once the socket is connected.
    const interval = socketConnected ? 30000 : POLL_INTERVAL_MS;
    pollTimerRef.current = setInterval(fetchLatest, interval);
    return () => clearInterval(pollTimerRef.current);
  }, [open, conversationId, fetchLatest, socketConnected]);

  // Socket subscription — pushes AI/merchant messages without polling.
  useEffect(() => {
    if (!bootstrap?.enabled || !tokenRef.current) return undefined;

    const sock = socketIo(`${getSocketUrl()}/storefront-chatbot`, {
      auth: { token: tokenRef.current },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
    socketRef.current = sock;

    sock.on('connect', () => setSocketConnected(true));
    sock.on('disconnect', () => setSocketConnected(false));
    sock.on('connect_error', (err) => {
      console.warn('[Chatbot] socket connect_error:', err?.message);
      setSocketConnected(false);
    });

    const appendIfNew = (payload) => {
      const msg = payload?.message;
      if (!msg || !msg.id) return;
      if (seenIdsRef.current.has(msg.id)) return;
      seenIdsRef.current.add(msg.id);
      setAiTyping(false);
      setMessages((prev) => [...prev, msg]);
    };

    sock.on('chatbot:ai_reply', appendIfNew);
    sock.on('chatbot:merchant_reply', appendIfNew);
    sock.on('chatbot:taken_over', () => {
      // Visitor-facing system message: a human just joined
      setMessages((prev) => [...prev, {
        id: `local-takeover-${Date.now()}`,
        role: 'SYSTEM',
        content: 'A team member just joined the chat.',
        createdAt: new Date().toISOString(),
        _local: true,
      }]);
    });

    return () => {
      sock.removeAllListeners();
      sock.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [bootstrap?.enabled]);

  // Join the conversation room whenever conversationId is known + socket alive
  useEffect(() => {
    if (!conversationId || !socketRef.current || !socketConnected) return undefined;
    socketRef.current.emit('conversation:join', { conversationId });
    return () => {
      try { socketRef.current?.emit('conversation:leave', { conversationId }); } catch { /* noop */ }
    };
  }, [conversationId, socketConnected]);

  // --- Auto-scroll thread on new message ----------------------------------

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, aiTyping]);

  // --- Send a visitor message ---------------------------------------------

  const sendMessage = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending || !tokenRef.current) return;

    setSending(true);
    const localMsg = {
      id: `local-${Date.now()}`,
      role: 'VISITOR',
      content: text,
      createdAt: new Date().toISOString(),
      _local: true,
    };
    setMessages((prev) => [...prev, localMsg]);
    setDraft('');
    setAiTyping(true);

    try {
      const res = await httpRef.current.post(
        `/storefront/${storeSlug}/chatbot/message`,
        {
          conversationId,
          text,
          pageContext: { path: window.location.pathname + window.location.search },
        },
        { headers: { Authorization: `Bearer ${tokenRef.current}` } },
      );
      const data = res?.data || {};
      if (data.conversationId && data.conversationId !== conversationId) {
        setConversationId(data.conversationId);
      }
      // Use the server's canonical message rows from the response directly —
      // polling closure may still have a stale conversationId on first send.
      const serverVisitor = data.visitorMessage;
      const serverAi = data.aiReply;
      setMessages((prev) => {
        // Drop the optimistic local row, then dedupe by id so we don't
        // collide with anything the socket or poll already delivered.
        const filtered = prev.filter((m) => m.id !== localMsg.id);
        const existingIds = new Set(filtered.map((m) => m.id));
        const next = filtered.slice();
        if (serverVisitor) {
          seenIdsRef.current.add(serverVisitor.id);
          if (!existingIds.has(serverVisitor.id)) {
            next.push(serverVisitor);
            existingIds.add(serverVisitor.id);
          }
        } else {
          next.push(localMsg);
        }
        if (serverAi) {
          seenIdsRef.current.add(serverAi.id);
          if (!existingIds.has(serverAi.id)) {
            next.push(serverAi);
          }
        }
        return next;
      });
      setAiTyping(false);
    } catch (err) {
      const serverMsg = err?.response?.data?.error;
      const status = err?.response?.status;
      // Log as a single string so the values are inline in Console, not
      // hidden behind an Object disclosure-triangle.
      console.error(
        `[Chatbot] send failed — status=${status ?? 'n/a'} serverMsg="${serverMsg ?? ''}" error="${err?.message ?? ''}"`,
      );
      setAiTyping(false);
      setMessages((prev) => [...prev, {
        id: `local-err-${Date.now()}`,
        role: 'SYSTEM',
        content: serverMsg
          ? `Could not send: ${serverMsg}`
          : 'Could not send. Please try again.',
        createdAt: new Date().toISOString(),
        _local: true,
      }]);
    } finally {
      setSending(false);
      // Restore focus to composer on desktop
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [draft, sending, storeSlug, conversationId, fetchLatest]);

  // --- Close handlers ------------------------------------------------------

  const handleDismiss = useCallback(() => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISSED_SESSION_KEY, '1');
    } catch { /* sessionStorage may be blocked */ }
  }, []);

  const handleDeleteHistory = useCallback(async () => {
    const ok = typeof window !== 'undefined'
      && window.confirm('Delete this chat and all your previous chats with this store?');
    if (!ok) return;
    try {
      await httpRef.current.delete(`/storefront/${storeSlug}/chatbot/visitor`, {
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });
      // Reset everything; the next message will bootstrap a brand-new visitor
      setMessages([]);
      setConversationId(null);
      seenIdsRef.current = new Set();
      if (typeof localStorage !== 'undefined') localStorage.removeItem(VISITOR_KEY);
      // Reload to re-bootstrap with a fresh anonId
      setBootstrap(null);
      setOpen(false);
      window.location.reload();
    } catch (err) {
      console.error('[Chatbot] delete-history failed:', err?.message);
    }
  }, [storeSlug]);

  // --- Live Activity tracking ---------------------------------------------
  // Mount as soon as the visitor JWT is in hand. The hook itself bails when
  // `enabled` is false or DNT is on.
  useLiveActivity({
    visitorId: bootstrap?.visitorId,
    token: tokenRef.current,
    storeSlug,
    enabled: !!(bootstrap?.enabled && bootstrap?.visitorId && tokenRef.current),
  });

  // --- Render --------------------------------------------------------------

  // Brand color (hooks must run on every render — keep above any early return).
  const brandColor = bootstrap?.settings?.brandColor
    || store?.themeConfig?.primaryColor
    || '#0d7463';
  const rootStyle = useMemo(() => ({ '--cb-brand': brandColor }), [brandColor]);

  // Don't render anything until we know whether the store has it enabled
  if (!bootstrap || bootstrap.enabled === false || bootstrapError) return null;

  const botName = bootstrap?.settings?.botDisplayName || 'Assistant';
  const responseHint = bootstrap?.settings?.responseTimeHint || 'We typically reply within minutes';
  const unreadCount = 0; // TODO: surface server-side unread count for the launcher badge

  if (!open) {
    return (
      <div className="cb-root" style={rootStyle}>
        <button
          type="button"
          className="cb-launcher"
          onClick={() => setOpen(true)}
          aria-label="Open chat"
        >
          <MessageCircle size={24} className="cb-launcher__icon" />
          {unreadCount > 0 && <span className="cb-launcher__badge">{unreadCount}</span>}
        </button>
      </div>
    );
  }

  return (
    <div className="cb-root" style={rootStyle}>
      <div className="cb-panel" role="dialog" aria-label="Chat with the store">
        <div className="cb-header">
          <div className="cb-header__avatar">
            {store?.logo ? <img src={store.logo} alt="" /> : (botName?.charAt(0) || 'A')}
          </div>
          <div className="cb-header__meta">
            <p className="cb-header__title">{botName} · {bootstrap?.storeName || store?.name || 'Chat'}</p>
            <p className="cb-header__sub">{responseHint}</p>
          </div>
          <button type="button" className="cb-header__close" onClick={handleDismiss} aria-label="Close chat">
            <X size={18} />
          </button>
        </div>

        <div className="cb-thread" ref={threadRef}>
          {messages.map((m) => (
            <MessageRow key={m.id} message={m} botName={botName} storeSlug={storeSlug} />
          ))}
          {aiTyping && (
            <div className="cb-typing" aria-label="Typing">
              <span className="cb-typing__dot" />
              <span className="cb-typing__dot" />
              <span className="cb-typing__dot" />
            </div>
          )}
        </div>

        <form
          className="cb-composer"
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
        >
          <textarea
            ref={inputRef}
            className="cb-composer__input"
            placeholder="Type your message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            rows={1}
            disabled={sending}
          />
          <button
            type="submit"
            className="cb-composer__send"
            disabled={sending || !draft.trim()}
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </form>

        <div className="cb-footer">
          <span>Powered by Revstar AI</span>
          <button type="button" className="cb-footer__link" onClick={handleDeleteHistory}>
            Delete chat history
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Single message row (handles attachments) -----------------------------

const MessageRow = ({ message, botName, storeSlug }) => {
  const role = String(message.role || '').toLowerCase();
  const attachments = Array.isArray(message.attachments) ? message.attachments : [];
  const productCards = attachments.filter((a) => a?.type === 'product_card');
  const others = attachments.filter((a) => a?.type !== 'product_card');

  // System messages render alone
  if (role === 'system') {
    return <div className="cb-msg cb-msg--system">{message.content}</div>;
  }

  return (
    <>
      {message.content?.trim() && (
        <div className={`cb-msg cb-msg--${role || 'ai'}`}>
          {(role === 'ai' || role === 'merchant') && (
            <span className="cb-msg__role">{role === 'merchant' ? 'Team' : botName}</span>
          )}
          {message.content}
        </div>
      )}
      {productCards.length > 0 && (
        <div className="cb-products">
          {productCards.map((p, i) => (
            <ProductCard key={`${message.id}-p${i}`} product={p} storeSlug={storeSlug} />
          ))}
        </div>
      )}
      {others.map((a, i) => (
        <VisitorAttachment key={`${message.id}-att-${i}`} attachment={a} role={role} />
      ))}
    </>
  );
};

// Render image / coupon / button_link in the visitor widget so the merchant's
// reply attachments are actually usable from the storefront side too.
const VisitorAttachment = ({ attachment: a, role }) => {
  const sideClass = `cb-msg cb-msg--${role || 'merchant'}`;
  if (a.type === 'image' && a.url) {
    return (
      <a href={a.url} target="_blank" rel="noopener noreferrer" className={sideClass} style={{ padding: 4, display: 'inline-block' }}>
        <img src={a.url} alt={a.name || ''} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 6, display: 'block' }} />
      </a>
    );
  }
  if (a.type === 'discount_code') {
    return (
      <div className={sideClass} style={{ padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.7 }}>Coupon</span>
        <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(13, 116, 99, 0.08)', color: '#0d7463' }}>{a.code}</code>
        {a.label && <span style={{ fontSize: 11.5, opacity: 0.8 }}>{a.label}</span>}
      </div>
    );
  }
  if (a.type === 'button_link' && a.url) {
    return (
      <a
        href={a.url}
        target="_blank"
        rel="noopener noreferrer"
        className={sideClass}
        style={{ padding: '8px 14px', textDecoration: 'none', fontSize: 13, fontWeight: 600, color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {a.label || 'Open link'}
        <span aria-hidden>↗</span>
      </a>
    );
  }
  return null;
};

const ProductCard = ({ product, storeSlug }) => {
  // Subdomain/custom-domain mounts use `/product/<slug>`; path mode uses
  // `/s/<storeSlug>/product/<slug>`. Detect from the current pathname so the
  // link works in both contexts.
  const isPathMode = typeof window !== 'undefined' && window.location.pathname.startsWith(`/s/${storeSlug}`);
  const prefix = isPathMode ? `/s/${storeSlug}` : '';
  const target = product.slug || product.productId;
  const href = target ? `${prefix}/product/${target}` : '#';
  return (
    <a className="cb-product" href={href}>
      {product.image && <img className="cb-product__img" src={product.image} alt="" />}
      <div className="cb-product__body">
        <p className="cb-product__name">{product.name}</p>
        {product.price !== undefined && (
          <p className="cb-product__price">
            {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(parseFloat(product.price) || 0)}
          </p>
        )}
      </div>
    </a>
  );
};

export default ChatbotWidget;
